import {
  Typography,
  Box,
  Chip,
  Avatar,
  Divider,
  Stack,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTenant } from "hooks/settings";
import { useListDeployedPlansQuery } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  hoverEmail,
  userDisplayName,
  userInitials,
} from "src/utils/userDisplay";
import { OverviewCard } from "./OverviewCard";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

const formatTimeAgo = (dateString: string) => {
  return dayjs(dateString).fromNow();
};

const formatDateTime = (dateString: string) => {
  const dayjsTimestamp = dayjs(dateString);
  return `${dayjsTimestamp.format("ll")} ${dayjsTimestamp.format("LT")}`;
};

export const CurrentlyDeployedPlan = () => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const formatNumber = useFormatNumber();
  const units = useUnitsFormatter(false);

  const { data, isLoading } = useListDeployedPlansQuery({
    tenantName: tenant,
    limit: 1,
    offset: 0,
  });
  const plan = data?.items[0];

  const formatCost = (amount: number) => {
    return `${formatNumber(0, true)(amount)}${units("cost")}`;
  };

  const formatSpecificCost = (amount: number) => {
    return `${formatNumber(2, true)(amount)}${units("specific_cost")}`;
  };

  const headerContent = (
    <Chip
      label={t("active")}
      color="success"
      variant="outlined"
      size="small"
      sx={{ ml: "auto" }}
    />
  );

  return (
    <OverviewCard
      title={t("currentlyDeployedPlan")}
      icon={PrecisionManufacturingIcon}
      headerContent={plan ? headerContent : null}
      buttonText={t("viewAllDeployedPlans")}
      buttonHref={`/${tenant}/deployed-plans`}
      delay={0.1}
    >
      {isLoading ? (
        <Skeleton variant="rectangular" height={250} />
      ) : plan ? (
        <Box sx={{ px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Tooltip title={hoverEmail(plan.deployed_by)}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: "0.8rem",
                  fontWeight: "fontWeightSemiBold",
                }}
              >
                {userInitials(plan.deployed_by)}
              </Avatar>
            </Tooltip>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "fontWeightSemiBold",
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                {plan.session_name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {userDisplayName(plan.deployed_by)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccessTimeIcon
                    sx={{ fontSize: 14, color: "text.secondary" }}
                  />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {formatTimeAgo(plan.deployed_at)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: "fontWeightMedium",
                  }}
                >
                  {t("cost")}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "fontWeightSemiBold",
                    color: "text.primary",
                  }}
                >
                  {formatSpecificCost(
                    plan.first_period_summary.cost_per_tonne_tapped
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: "fontWeightMedium",
                  }}
                >
                  {t("yield")}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "fontWeightSemiBold" }}
                >
                  {formatNumber(1)(plan.first_period_summary.yield_percent)}
                  {units("yield", false)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: "fontWeightMedium",
                  }}
                >
                  {t("totalHeats")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "fontWeightSemiBold",
                    color: "text.primary",
                  }}
                >
                  {plan.first_period_summary.num_heats}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: "fontWeightMedium",
                  }}
                >
                  {t("totalCost")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "fontWeightSemiBold",
                    color: "text.primary",
                  }}
                >
                  {formatCost(plan.first_period_summary.total_cost)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: "fontWeightMedium",
                  }}
                >
                  {t("startTime")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "fontWeightSemiBold",
                    color: "text.primary",
                  }}
                >
                  {formatDateTime(plan.start_at)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: "fontWeightMedium",
                  }}
                >
                  {t("endTime")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "fontWeightSemiBold",
                    color: "text.primary",
                  }}
                >
                  {formatDateTime(plan.end_at)}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ px: 2 }}>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t("noDeployedPlan")}
          </Typography>
        </Box>
      )}
    </OverviewCard>
  );
};
